import {
  createDirectus,
  rest,
  authentication,
  realtime,
  readItems,
  readItem,
  createItem,
  updateItem,
  deleteItem,
  readMe,
  updateMe,
  readSingleton,
  updateSingleton,
  readUser,
  readUsers,
  updateUser,
  uploadFiles,
  readFile,
  readFiles,
  updateFile,
  refresh,
  aggregate,
  readComments,
  createComment,
  updateComment,
  deleteComment
} from '@directus/sdk';

const { VITE_apiUrl: apiUrl } = import.meta.env;

// localStorage key for storing access token (needed for WebSocket auth)
const TOKEN_STORAGE_KEY = 'directus_access_token';

// Create the new v11 client with all composables
const client = createDirectus(apiUrl)
  .with(authentication('cookie', {
    credentials: 'include'
  }))
  .with(rest({
    credentials: 'include'
  }))
  .with(realtime({
    // Use 'public' mode - server authenticates via session cookie
    // The cookie is sent with WebSocket since we're on same root domain (.chemspeed.com)
    authMode: 'public',
    reconnect: {
      delay: 2000,    // Wait 2 seconds between attempts
      retries: 50     // Try for ~100 seconds total (handles longer restarts)
    }
  }));

// On init, restore token from localStorage if available
const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
if (storedToken) {
  client.setToken(storedToken);
}

// Helper to store token in localStorage and client memory
function storeAccessToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    client.setToken(token);
  }
}

// Helper to clear stored token (on logout)
function clearAccessToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

// Track refresh state for interceptor-like behavior
let isRefreshing = false;
let refreshPromise = null;

// Helper to wrap requests with auto-refresh on 401
async function withAutoRefresh(requestFn) {
  try {
    return await requestFn();
  } catch (error) {
    // Detect network errors (offline, DNS failure, server unreachable)
    // These should not trigger Sentry — they're transient connectivity issues
    const errorMsg = error?.message || '';
    const isNetworkError = errorMsg === 'Failed to fetch'
      || errorMsg.includes('NetworkError')
      || errorMsg.includes('Network request failed')
      || error?.name === 'TypeError' && !error?.response;
    if (isNetworkError) {
      console.warn('Network error during API request:', errorMsg);
      throw error; // Re-throw but Sentry won't capture handled network errors
    }

    const status = error?.response?.status || error?.errors?.[0]?.extensions?.code;
    const isAuthError = status === 401 || status === 'TOKEN_EXPIRED' || status === 'FORBIDDEN';

    if (isAuthError) {
      // Avoid redirect loops on login pages
      if (window.location.pathname.includes('/login') ||
          window.location.pathname.includes('/auth/callback')) {
        throw error;
      }

      // If already refreshing, wait for that to complete
      if (isRefreshing) {
        try {
          await refreshPromise;
          return requestFn();
        } catch {
          throw error;
        }
      }

      isRefreshing = true;
      refreshPromise = client.request(refresh('cookie'))
        .then((result) => {
          isRefreshing = false;
          refreshPromise = null;
          // Store the new access token for WebSocket auth
          if (result?.access_token) {
            storeAccessToken(result.access_token);
          }
          return result;
        })
        .catch(() => {
          isRefreshing = false;
          refreshPromise = null;
          clearAccessToken();
          console.warn('Session expired, redirecting to login...');
          window.location.href = '/login?expired=1';
          return new Promise(() => {});
        });

      try {
        await refreshPromise;
        return requestFn();
      } catch {
        throw error;
      }
    }
    throw error;
  }
}

// ===========================================
// COMPATIBILITY LAYER (mimics v10 API)
// ===========================================

// Core collections that need special handling in v11 SDK
const CORE_COLLECTIONS = ['directus_users', 'directus_files', 'directus_folders', 'directus_comments'];

const directus = {
  // Raw client for new code / WebSocket access
  client,

  // items(collection) compatibility
  items(collection) {
    // Handle directus_users specially - v11 SDK doesn't allow readItems on core collections
    if (collection === 'directus_users') {
      return {
        async readByQuery(query = {}) {
          // Extract meta from query - if present, we need to fetch total count.
          // Data + count fire in parallel (Promise.all) so the second round-trip
          // doesn't add latency on top of the first.
          const { meta: requestMeta, ...queryWithoutMeta } = query;
          const needsCount = !!(requestMeta && query.limit && query.limit !== -1);

          const dataPromise = withAutoRefresh(() =>
            client.request(readUsers(queryWithoutMeta))
          );
          const countPromise = needsCount
            ? withAutoRefresh(() =>
                client.request(aggregate('directus_users', {
                  aggregate: { count: '*' },
                  query: query.filter ? { filter: query.filter } : undefined
                }))
              ).catch((countError) => {
                console.warn('Failed to fetch user count:', countError);
                return null;
              })
            : Promise.resolve(null);

          const [result, countResult] = await Promise.all([dataPromise, countPromise]);

          let filterCount = Array.isArray(result) ? result.length : 0;
          let totalCount = filterCount;
          if (countResult && countResult[0] && countResult[0].count !== undefined) {
            filterCount = parseInt(countResult[0].count, 10);
            totalCount = filterCount;
          }

          return {
            data: result,
            meta: {
              filter_count: filterCount,
              total_count: totalCount
            }
          };
        },
        async readOne(id, query = {}) {
          return withAutoRefresh(() =>
            client.request(readUser(id, query))
          );
        },
        async updateOne(id, data) {
          return withAutoRefresh(() =>
            client.request(updateUser(id, data))
          );
        },
        createOne: () => { throw new Error('Use directus.users API to create users'); },
        deleteOne: () => { throw new Error('Use directus.users API to delete users'); }
      };
    }

    // Handle directus_files specially
    if (collection === 'directus_files') {
      return {
        async readByQuery(query = {}) {
          const { meta: requestMeta, ...queryWithoutMeta } = query;
          const needsCount = !!(requestMeta && query.limit && query.limit !== -1);

          const dataPromise = withAutoRefresh(() =>
            client.request(readFiles(queryWithoutMeta))
          );
          const countPromise = needsCount
            ? withAutoRefresh(() =>
                client.request(aggregate('directus_files', {
                  aggregate: { count: '*' },
                  query: query.filter ? { filter: query.filter } : undefined
                }))
              ).catch((countError) => {
                console.warn('Failed to fetch file count:', countError);
                return null;
              })
            : Promise.resolve(null);

          const [result, countResult] = await Promise.all([dataPromise, countPromise]);

          let filterCount = Array.isArray(result) ? result.length : 0;
          let totalCount = filterCount;
          if (countResult && countResult[0] && countResult[0].count !== undefined) {
            filterCount = parseInt(countResult[0].count, 10);
            totalCount = filterCount;
          }

          return {
            data: result,
            meta: {
              filter_count: filterCount,
              total_count: totalCount
            }
          };
        },
        async readOne(id, query = {}) {
          return withAutoRefresh(() =>
            client.request(readFile(id, query))
          );
        },
        async createOne(formData) {
          return withAutoRefresh(() =>
            client.request(uploadFiles(formData))
          );
        },
        async updateOne(id, data) {
          return withAutoRefresh(() =>
            client.request(updateFile(id, data))
          );
        },
        deleteOne: () => { throw new Error('Use directus.files API to delete files'); }
      };
    }

    // Handle directus_comments specially
    if (collection === 'directus_comments') {
      return {
        async readByQuery(query = {}) {
          const { meta: requestMeta, ...queryWithoutMeta } = query;

          const result = await withAutoRefresh(() =>
            client.request(readComments(queryWithoutMeta))
          );

          return {
            data: result,
            meta: {
              filter_count: Array.isArray(result) ? result.length : 0,
              total_count: Array.isArray(result) ? result.length : 0
            }
          };
        },
        async createOne(data) {
          return withAutoRefresh(() =>
            client.request(createComment(data))
          );
        },
        async updateOne(id, data) {
          return withAutoRefresh(() =>
            client.request(updateComment(id, data))
          );
        },
        async deleteOne(id) {
          return withAutoRefresh(() =>
            client.request(deleteComment(id))
          );
        }
      };
    }

    // Standard collection handling
    return {
      async readByQuery(query = {}) {
        const { meta: requestMeta, ...queryWithoutMeta } = query;
        const needsCount = !!(requestMeta && query.limit && query.limit !== -1);

        const dataPromise = withAutoRefresh(() =>
          client.request(readItems(collection, queryWithoutMeta))
        );
        const countPromise = needsCount
          ? withAutoRefresh(() =>
              client.request(aggregate(collection, {
                aggregate: { count: '*' },
                query: query.filter ? { filter: query.filter } : undefined
              }))
            ).catch((countError) => {
              console.warn('Failed to fetch total count:', countError);
              return null;
            })
          : Promise.resolve(null);

        const [result, countResult] = await Promise.all([dataPromise, countPromise]);

        let filterCount = Array.isArray(result) ? result.length : 0;
        let totalCount = filterCount;
        if (countResult && countResult[0] && countResult[0].count !== undefined) {
          filterCount = parseInt(countResult[0].count, 10);
          totalCount = filterCount;
        }

        return {
          data: result,
          meta: {
            filter_count: filterCount,
            total_count: totalCount
          }
        };
      },

      async readOne(id, query = {}) {
        return withAutoRefresh(() =>
          client.request(readItem(collection, id, query))
        );
      },

      async createOne(data) {
        return withAutoRefresh(() =>
          client.request(createItem(collection, data))
        );
      },

      async updateOne(id, data) {
        return withAutoRefresh(() =>
          client.request(updateItem(collection, id, data))
        );
      },

      async deleteOne(id) {
        return withAutoRefresh(() =>
          client.request(deleteItem(collection, id))
        );
      }
    };
  },

  // singleton(collection) — for Directus singleton collections, which have no
  // /:id route. Use .read()/.update() instead of items(collection).readOne(1).
  singleton(collection) {
    return {
      async read(query = {}) {
        return withAutoRefresh(() =>
          client.request(readSingleton(collection, query))
        );
      },
      async update(data, query = {}) {
        return withAutoRefresh(() =>
          client.request(updateSingleton(collection, data, query))
        );
      }
    };
  },

  // users compatibility
  users: {
    me: {
      async read(query = {}) {
        return withAutoRefresh(() =>
          client.request(readMe(query))
        );
      },
      async update(data) {
        return withAutoRefresh(() =>
          client.request(updateMe(data))
        );
      }
    },

    async readOne(id, query = {}) {
      return withAutoRefresh(() =>
        client.request(readUser(id, query))
      );
    },

    async readByQuery(query = {}) {
      const { meta: requestMeta, ...queryWithoutMeta } = query;

      const result = await withAutoRefresh(() =>
        client.request(readUsers(queryWithoutMeta))
      );

      let filterCount = Array.isArray(result) ? result.length : 0;
      let totalCount = filterCount;

      if (requestMeta && query.limit && query.limit !== -1) {
        try {
          const aggregateQuery = {};
          if (query.filter) aggregateQuery.filter = query.filter;

          const countResult = await withAutoRefresh(() =>
            client.request(aggregate('directus_users', {
              aggregate: { count: '*' },
              query: Object.keys(aggregateQuery).length > 0 ? aggregateQuery : undefined
            }))
          );
          if (countResult && countResult[0] && countResult[0].count !== undefined) {
            filterCount = parseInt(countResult[0].count, 10);
            totalCount = filterCount;
          }
        } catch (countError) {
          console.warn('Failed to fetch user count:', countError);
        }
      }

      return {
        data: result,
        meta: {
          filter_count: filterCount,
          total_count: totalCount
        }
      };
    },

    async updateOne(id, data) {
      return withAutoRefresh(() =>
        client.request(updateUser(id, data))
      );
    }
  },

  // files compatibility
  files: {
    async createOne(formData) {
      return withAutoRefresh(() =>
        client.request(uploadFiles(formData))
      );
    },

    async readOne(id, query = {}) {
      return withAutoRefresh(() =>
        client.request(readFile(id, query))
      );
    },

    async readByQuery(query = {}) {
      const { meta: requestMeta, ...queryWithoutMeta } = query;

      const result = await withAutoRefresh(() =>
        client.request(readFiles(queryWithoutMeta))
      );

      let filterCount = Array.isArray(result) ? result.length : 0;
      let totalCount = filterCount;

      if (requestMeta && query.limit && query.limit !== -1) {
        try {
          const aggregateQuery = {};
          if (query.filter) aggregateQuery.filter = query.filter;

          const countResult = await withAutoRefresh(() =>
            client.request(aggregate('directus_files', {
              aggregate: { count: '*' },
              query: Object.keys(aggregateQuery).length > 0 ? aggregateQuery : undefined
            }))
          );
          if (countResult && countResult[0] && countResult[0].count !== undefined) {
            filterCount = parseInt(countResult[0].count, 10);
            totalCount = filterCount;
          }
        } catch (countError) {
          console.warn('Failed to fetch file count:', countError);
        }
      }

      return {
        data: result,
        meta: {
          filter_count: filterCount,
          total_count: totalCount
        }
      };
    },

    async updateOne(id, data) {
      return withAutoRefresh(() =>
        client.request(updateFile(id, data))
      );
    }
  },

  // auth compatibility
  auth: {
    async refresh() {
      return client.request(refresh('cookie'));
    }
  },

  // transport compatibility stub (deprecated - for code that references it)
  transport: {
    axios: {
      defaults: { withCredentials: true },
      interceptors: {
        response: {
          use: () => {}
        }
      }
    }
  }
};

// ===========================================
// WEBSOCKET / REALTIME HELPER
// ===========================================

// With authMode: 'public', server authenticates via session cookie
// No token needed from client side
async function ensureTokenForWebSocket() {
  return true;
}

export function useRealtimeSubscription() {
  return {
    /**
     * Subscribe to collection changes via WebSocket
     * @param {string} collection - Collection name
     * @param {Object} options - Subscription options
     * @param {string|string[]} options.event - Event type(s): 'create', 'update', 'delete', or array of these
     * @param {Object} options.query - Query options (fields, filter, etc.)
     * @param {Function} options.onData - Callback for data events
     * @param {Function} options.onError - Callback for errors
     */
    async subscribe(collection, options = {}) {
      const { event = ['create', 'update', 'delete'], query = {}, onData, onError } = options;

      try {
        await ensureTokenForWebSocket();

        // Normalize event to array
        const events = Array.isArray(event) ? event : [event];
        const subscriptions = [];
        const unsubscribeFns = [];

        // Subscribe to each event type
        for (const eventType of events) {
          try {
            const { subscription, unsubscribe } = await client.subscribe(collection, {
              event: eventType,
              query
            });

            subscriptions.push({ subscription, eventType });
            unsubscribeFns.push(unsubscribe);

            // Handle incoming messages in background for this subscription
            (async () => {
              try {
                for await (const message of subscription) {
                  // Check if this is an error message from the server
                  if (message.type === 'subscribe' && message.status === 'error') {
                    console.error(`WebSocket subscription error (${eventType}):`, JSON.stringify(message, null, 2));
                    if (onError) onError(message);
                    return;
                  }
                  // Add event type to message for caller convenience
                  if (onData) onData({ ...message, eventType });
                }
              } catch (err) {
                console.error(`WebSocket error (${eventType}):`, err);
                if (onError) onError(err);
              }
            })();
          } catch (err) {
            console.error(`Failed to subscribe to ${collection} (${eventType}):`, err);
            // Continue with other events even if one fails
          }
        }

        // Return combined unsubscribe function
        return {
          unsubscribe: () => {
            unsubscribeFns.forEach(fn => {
              try {
                fn();
              } catch (err) {
                // Ignore errors when connection is already closed
                // This can happen when component unmounts after WebSocket disconnects
              }
            });
          }
        };
      } catch (error) {
        if (onError) onError(error);
        throw error;
      }
    }
  };
}

// ===========================================
// SESSION HEARTBEAT (cookie-based auth)
// ===========================================
// Proactively check if the session cookie is still valid.
// Uses a lightweight HEAD request to /users/me instead of refresh('cookie'),
// since SSO auth uses session cookies — no refresh token exists.

let heartbeatInterval = null;

export function startSessionHeartbeat() {
  if (heartbeatInterval) return;
  heartbeatInterval = setInterval(async () => {
    if (window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/auth/callback')) {
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/users/me`, {
        method: 'HEAD',
        credentials: 'include'
      });
      if (!response.ok) {
        clearAccessToken();
        window.location.href = '/login?expired=1';
      }
    } catch {
      // Network error — don't redirect, user might be offline temporarily
    }
  }, 2 * 60 * 60 * 1000); // 2 hours
}

export function stopSessionHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

// Export both for compatibility and new usage
export default directus;
export { client, withAutoRefresh, storeAccessToken, clearAccessToken };
